import { Heart, MapPin, Star, TrendUp } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toAbsolute } from "@/lib/apiClient";

const typeTone = {
    trip: { label: "Trip", accent: "var(--gold)", bg: "rgba(212,175,55,0.12)", border: "rgba(212,175,55,0.3)" },
    traveldate: { label: "Date", accent: "#F4A28F", bg: "rgba(244,162,143,0.12)", border: "rgba(244,162,143,0.3)" },
};

function formatCompact(value) {
    return new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value || 0);
}

function destinationLabel(destination = {}) {
    return destination.destination_label || [destination.city_name, destination.country_name].filter(Boolean).join(", ") || destination.country_name || "Destination pending";
}

function dateMeta(item) {
    if (item.content_type === "trip") {
        if (item.summary?.trip_days) return `${item.summary.trip_days} day${item.summary.trip_days === 1 ? "" : "s"}`;
        return item.updated_at ? new Date(item.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
    }
    if (item.summary?.date_duration_hours) return `${item.summary.date_duration_hours} hr${item.summary.date_duration_hours === 1 ? "" : "s"}`;
    return item.updated_at ? new Date(item.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
}

function FiverrRating({ metrics = {} }) {
    const count = metrics.ratings_count || 0;
    const rawAvg = metrics.ratings_avg;
    const avg = typeof rawAvg === "number" ? rawAvg.toFixed(1) : (rawAvg ? Number(rawAvg).toFixed(1) : null);

    if (!count || !avg) {
        return (
            <div className="inline-flex items-center gap-1 text-xs font-medium text-white/50">
                <Star size={13} weight="fill" className="text-[var(--gold)]" />
                <span>New</span>
            </div>
        );
    }

    return (
        <div className="inline-flex items-center gap-1 text-xs font-semibold text-white">
            <Star size={13} weight="fill" className="text-[var(--gold)]" />
            <span>{avg}</span>
            <span className="text-white/45 font-normal">({formatCompact(count)})</span>
        </div>
    );
}

function secondaryCopy(item) {
    if (item.summary?.subtitle) return item.summary.subtitle;
    if (item.content_type === "trip" && item.summary?.route_preview?.length) return item.summary.route_preview.join(" · ");
    if (item.content_type === "trip" && item.summary?.trip_style) return item.summary.trip_style;
    return null;
}

export default function DiscoverPlanCard({
    item,
    onLike,
    onAdd,
    onShare,
    busy = false,
    addLabel,
    profileMode = false,
}) {
    const tone = typeTone[item.content_type] || typeTone.trip;
    const detailHref = item.content_type === "trip" ? `/discover/trips/${item.source_id}` : `/discover/dates/${item.source_id}`;
    const profileHref = item.owner?.handle ? `/profile/${item.owner.handle}` : null;
    const metaLabel = dateMeta(item);
    const supportCopy = secondaryCopy(item);
    return (
        <motion.article
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="group rounded-[28px] overflow-hidden"
            style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.08)" }}
            data-testid={`${item.content_type}-card-${item.source_id}`}
        >
            <Link to={detailHref} className="block" data-testid={`${item.content_type}-card-open-${item.source_id}`}>
                <div className="relative aspect-[16/10] bg-black/20 overflow-hidden">
                    {item.hero_image ? (
                        <img src={toAbsolute(item.hero_image)} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    ) : (
                        <div className="w-full h-full" style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.18), rgba(42,67,101,0.22))" }} />
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.08) 22%, rgba(5,10,17,0.92) 100%)" }} />
                    <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.24em]" style={{ background: tone.bg, color: tone.accent, border: `1px solid ${tone.border}` }} data-testid={`${item.content_type}-card-type-${item.source_id}`}>
                            <TrendUp size={10} weight="fill" /> {tone.label}
                        </span>
                        <button
                            type="button"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onLike?.(item);
                            }}
                            className="w-10 h-10 rounded-full grid place-items-center transition-transform duration-200 hover:scale-105 active:scale-95"
                            style={{
                                background: item.viewer?.liked ? "rgba(244,162,143,0.25)" : "rgba(5,10,17,0.65)",
                                border: `1px solid ${item.viewer?.liked ? "rgba(244,162,143,0.5)" : "rgba(255,255,255,0.12)"}`,
                                color: item.viewer?.liked ? "#F4A28F" : "white",
                                backdropFilter: "blur(8px)",
                            }}
                            aria-label={item.viewer?.liked ? "Unlike" : "Like"}
                            data-testid={`${item.content_type}-card-like-${item.source_id}`}
                        >
                            <Heart size={18} weight={item.viewer?.liked ? "fill" : "regular"} />
                        </button>
                    </div>
                    <div className="absolute left-4 right-4 bottom-3">
                        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/65 mb-1.5" data-testid={`${item.content_type}-card-destination-${item.source_id}`}>
                            <MapPin size={11} color={tone.accent} weight="fill" /> {destinationLabel(item.destination)}
                        </div>
                        <h3 className="font-serif text-xl sm:text-2xl text-white leading-tight line-clamp-2" data-testid={`${item.content_type}-card-title-${item.source_id}`}>{item.title}</h3>
                        {supportCopy ? <p className="text-xs sm:text-sm text-white/72 mt-1.5 line-clamp-1" data-testid={`${item.content_type}-card-subtitle-${item.source_id}`}>{supportCopy}</p> : null}
                    </div>
                </div>
            </Link>

            <div className="p-3.5 sm:p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="min-w-0">
                        {profileHref ? (
                            <Link to={profileHref} className="inline-flex items-center gap-2 min-w-0" data-testid={`${item.content_type}-card-creator-link-${item.source_id}`}>
                                <img src={toAbsolute(item.owner?.avatar_url) || `https://api.dicebear.com/7.x/notionists/svg?seed=${item.owner?.handle || item.owner?.user_id || "passport"}`} alt={item.owner?.name || item.owner?.handle || "Creator"} className="w-8 h-8 rounded-full object-cover ring-1 ring-white/10" />
                                <div className="min-w-0">
                                    <div className="text-xs sm:text-sm font-medium text-white truncate" data-testid={`${item.content_type}-card-creator-name-${item.source_id}`}>{item.owner?.name || item.owner?.handle || "Passport traveler"}</div>
                                    <div className="text-[11px] text-white/50 truncate">@{item.owner?.handle || "traveler"}</div>
                                </div>
                            </Link>
                        ) : (
                            <div className="text-xs sm:text-sm text-white/70">Passport traveler</div>
                        )}
                    </div>
                    <FiverrRating metrics={item.metrics} />
                </div>

                <div className="flex items-center gap-1.5 text-xs text-white/60 mb-2.5" data-testid={`${item.content_type}-card-likes-count-${item.source_id}`}>
                    <Heart size={12} weight="fill" className="text-[#F4A28F]" />
                    <span>{formatCompact(item.metrics?.likes_count || 0)} {(item.metrics?.likes_count || 0) === 1 ? "like" : "likes"}</span>
                </div>

                <div className="flex flex-wrap gap-1.5" data-testid={`${item.content_type}-card-location-meta-${item.source_id}`}>
                    <span className="rounded-full px-2.5 py-1 text-[10px]" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.78)" }} data-testid={`${item.content_type}-card-country-${item.source_id}`}>
                        {item.destination?.country_name || "Country pending"}
                    </span>
                    {item.destination?.city_name ? (
                        <span className="rounded-full px-2.5 py-1 text-[10px]" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.78)" }} data-testid={`${item.content_type}-card-city-${item.source_id}`}>
                            {item.destination.city_name}
                        </span>
                    ) : null}
                    {metaLabel ? (
                        <span className="rounded-full px-2.5 py-1 text-[10px]" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: tone.accent }} data-testid={`${item.content_type}-card-meta-${item.source_id}`}>
                            {metaLabel}
                        </span>
                    ) : null}
                </div>
            </div>
        </motion.article>
    );
}
